import type { MutationResult, QueryResult } from '../shared/types';

import type { ScoredLaksvelParameter, SmoltLaksvelParameter } from './laksvelParameters';

export type SmoltReportStatus = 'finished' | 'warning' | 'error';

export interface SmoltReportWarnings {
    weightLowFrequency: boolean;
    weightDataLatency: boolean;
    /**
     * Delivery more than 3 days after the last measurement day. Reports that store it show it in
     * place of `weightDataLatency`, which the API then reports as false.
     */
    dataEndsBeforeDelivery: boolean;
    weightPeriodTooShort: boolean;
    welfareLowCoverage: boolean;
    welfareInsufficientSample: boolean;
}

export interface SmoltReportListItem {
    id: number;
    cageId: number;
    /** Fish group the report was computed for; null when it was computed for the cage. */
    fishGroupId: number | null;
    status: SmoltReportStatus;
    createdAt: Date;
    stockingDate: string;
    lastFeedingDate: string | null;
    deliveryDate: string;
    warnings: SmoltReportWarnings;
}

/**
 * Placeholder row shown in the list while a report is being created (no polling).
 * Mimics Harvest Report UX: show a row with "creating" status until the API returns.
 */
export interface CreatingReportPlaceholder {
    id: 'creating';
    status: 'created';
    cageId: number;
    /** The fish group the report is being created for, so it is marked like the stored one. */
    fishGroupId: number | null;
    stockingDate: string;
    lastFeedingDate: string | null;
    deliveryDate: string;
    createdAt: null;
    warnings: SmoltReportWarnings;
}

export type SmoltReportListItemRow =
    | (SmoltReportListItem & {
          onClick?: () => void;
          onDelete?: () => void;
          isDeleting?: boolean;
      })
    | (CreatingReportPlaceholder & {
          onClick?: () => void;
          onDelete?: () => void;
          isDeleting?: boolean;
      });

export function isCreatingPlaceholder(
    row: SmoltReportListItemRow
): row is CreatingReportPlaceholder {
    return row.id === 'creating';
}

export interface CreateSmoltReportRequest {
    stockingDate: string;
    deliveryDate: string;
    lastFeedingDate?: string;
    /** The Laksvel parameters the report shows; may be empty. */
    laksvelParameters: SmoltLaksvelParameter[];
}

/** Form/UI payload for creating a smolt report; mapped to API via mappers. */
export type CreateSmoltReportBody = CreateSmoltReportRequest;

export interface CreateSmoltReportVariables {
    cageId: number;
    requestBody: CreateSmoltReportRequest;
    /** Compute the report for this fish group (`fg`) instead of the cage. */
    fishGroupId?: number;
}

export interface SmoltReportCreated {
    reportId: number;
    cageId: number;
    /** Fish group the report was computed for; null when it was computed for the cage. */
    fishGroupId: number | null;
    stockingDate: string;
    lastFeedingDate: string | null;
    deliveryDate: string;
    status: SmoltReportStatus;
    warnings: SmoltReportWarnings;
    createdAt: Date;
}

export interface WeightDistributionBucket {
    from: number | null;
    to: number | null;
    count: number | null;
    percentage: number | null;
}

export interface DailyWeightTimeSeries {
    day: string;
    liveWeight: number | null;
    liveWeightTrend: number | null;
    sgr: number | null;
    growth: number | null;
    fishHeight: number | null;
    fishLength: number | null;
    cv: number | null;
    /** Fish measured that day. Null on reports stored before the daily count was kept. */
    numMeasurements: number | null;
}

export interface SmoltWeightData {
    numMeasurements: number;
    liveWeight: number | null;
    /** Living weight on the last feeding day. Null when the report has none, or predates it. */
    liveWeightAtLastFeeding: number | null;
    /** Midpoint of the lightest occupied distribution bucket, in grams. */
    smallestFish: number | null;
    /** Midpoint of the heaviest occupied distribution bucket, in grams. */
    largestFish: number | null;
    growth: number | null;
    sgr: number | null;
    conditionFactor: number | null;
    cv: number | null;
    distribution: WeightDistributionBucket[];
    /** `YYYY-MM-DD` the distribution is from (the newest on or before delivery); null without one. */
    distributionDay: string | null;
    timeSeries: DailyWeightTimeSeries[];
    /**
     * `YYYY-MM-DD` of the last weight measurement. Null when there is none, and on reports stored
     * before it was recorded, which keep their original data-latency wording.
     */
    lastMeasurementDay: string | null;
}

export interface DailyWounds {
    day: string;
    acute: number | null;
    healing: number | null;
}

export interface WoundsData {
    numMeasurements: number;
    acute: number | null;
    healing: number | null;
    development: DailyWounds[];
}

export interface DailyLaksvelScore {
    day: string;
    score1: number | null;
    score2: number | null;
    score3: number | null;
}

export interface LaksvelParameterData {
    numMeasurements: number;
    score1: number | null;
    score2: number | null;
    score3: number | null;
    development: DailyLaksvelScore[];
}

export interface LaksvelParameterEntry extends LaksvelParameterData {
    parameter: ScoredLaksvelParameter;
}

export interface SmoltWelfareData {
    /**
     * `YYYY-MM-DD` the welfare window ends on, the last welfare measurement day. Null on reports
     * stored before it was recorded, whose window ended on the delivery date.
     */
    windowEnd: string | null;
    /**
     * The Laksvel parameters the report shows, in report order. Reports made before the choice
     * show the default four; an empty list shows no welfare parameter at all.
     */
    includedParameters: SmoltLaksvelParameter[];
    numMeasurements: number;
    /** Null when body wounds are not included. */
    fishWithoutWounds: number | null;
    /** Null when body wounds are not included. */
    totalWounds: number | null;
    /** Null when body wounds are not included. */
    wounds: WoundsData | null;
    /** The included score parameters with figures, in report order. */
    laksvelParameters: LaksvelParameterEntry[];
}

export interface SmoltReportDetail {
    id: number;
    status: SmoltReportStatus;
    stockingDate: string;
    lastFeedingDate: string | null;
    deliveryDate: string;
    createdAt: Date;
    cageId: number;
    /** Fish group the report was computed for; null when it was computed for the cage. */
    fishGroupId: number | null;
    warnings: SmoltReportWarnings;
    weightData: SmoltWeightData;
    welfareData: SmoltWelfareData;
    /**
     * False when the report stored no welfare or Laksvel section: no LAKSVEL licence on the
     * delivery day, or a fish-group report made before fish-group welfare estimates existed.
     */
    hasWelfareData: boolean;
}

// Hook result types
export interface UseGetListReportsResult extends QueryResult<SmoltReportListItem[]> {}

export interface UseGetReportResult extends QueryResult<SmoltReportDetail> {}

export interface UsePostCreateReportResult
    extends MutationResult<SmoltReportCreated, CreateSmoltReportVariables> {}

export interface UseDeleteReportVariables {
    cageId: number;
    reportId: number;
}

export interface UseDeleteReportResult extends MutationResult<void, UseDeleteReportVariables> {}
